import React, { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Phone, Video, PhoneOff } from "lucide-react";

/**
 * Active call banner — polls `/workspace/{wid}/call/active` so every
 * participant in the workspace sees when someone else has started a call and
 * can join the SAME Jitsi room (instead of starting a fresh, isolated call).
 *
 * Props:
 *   workspaceId   – id of the idea / guidance request
 *   currentUserId – to skip the banner for the starter themself
 *   onJoin(call)  – called with the active-call doc; parent should mount the
 *                   EmbeddedMeetPanel using call.room_name / call.audio_only.
 */
export default function ActiveCallBanner({ workspaceId, currentUserId, onJoin }) {
  const [active, setActive] = useState(null);
  const [prevId, setPrevId] = useState(null);

  const refresh = useCallback(async () => {
    if (!workspaceId) return;
    try {
      const r = await api.get(`/workspace/${workspaceId}/call/active`);
      const doc = r.data && r.data.id ? r.data : null;
      setActive(doc);
    } catch {
      setActive(null);
    }
  }, [workspaceId]);

  useEffect(() => {
    refresh();
    const t = setInterval(refresh, 5000);
    return () => clearInterval(t);
  }, [refresh]);

  // Audible / visual ping when a NEW call starts (i.e. id changed) and the
  // current user did not initiate it.
  useEffect(() => {
    if (!active) {
      setPrevId(null);
      return;
    }
    if (active.id !== prevId) {
      setPrevId(active.id);
      if (active.started_by_user_id !== currentUserId) {
        try {
          // Short call-tone using WebAudio (no asset shipping cost).
          const ctx = new (window.AudioContext || window.webkitAudioContext)();
          const o = ctx.createOscillator();
          const g = ctx.createGain();
          o.type = "sine";
          o.frequency.value = 880;
          g.gain.value = 0.04;
          o.connect(g).connect(ctx.destination);
          o.start();
          setTimeout(() => { o.frequency.value = 660; }, 220);
          setTimeout(() => { o.stop(); ctx.close(); }, 480);
        } catch { /* audio blocked is fine */ }
      }
    }
  }, [active, prevId, currentUserId]);

  if (!active) return null;
  const mine = active.started_by_user_id === currentUserId;
  return (
    <div
      className={`flex items-center gap-3 px-4 py-3 rounded-sm border-2 ${
        mine
          ? "bg-emerald-50 border-emerald-300"
          : "bg-amber-50 border-amber-400 animate-pulse shadow-lg"
      }`}
      data-testid="active-call-banner"
    >
      <span className={`w-3 h-3 rounded-full ${mine ? "bg-emerald-500" : "bg-rose-500 animate-ping"}`} />
      {active.audio_only ? (
        <Phone className="w-4 h-4 text-kb-ink-muted" />
      ) : (
        <Video className="w-4 h-4 text-kb-ink-muted" />
      )}
      <div className="flex-1 min-w-0 text-sm">
        <span className="font-semibold text-kb-ink">{active.started_by_name || "Someone"}</span>
        <span className="text-kb-ink"> started {mine ? "an" : "a LIVE"} {active.audio_only ? "audio" : "video"} call</span>
        <span className="kb-mono text-[10px] uppercase tracking-wider text-kb-ink-muted ml-2 inline-flex items-center gap-1">
          · {active.provider === "google_meet" ? "Google Meet" : "Daily.co · in-app"}
        </span>
        {mine && (
          <div className="kb-mono text-[10px] uppercase tracking-wider text-emerald-700 mt-0.5">
            ✓ All participants notified — they can click <em>Join call</em> from their workspace
          </div>
        )}
      </div>
      {/* Always show the Join button — the starter also needs to be able to
          rejoin after closing the panel or navigating away. */}
      <button
        onClick={() => onJoin?.(active)}
        className="kb-btn-primary text-sm font-semibold px-4"
        data-testid="active-call-join-btn"
      >
        {mine ? "Rejoin" : "Join call"}
      </button>
      {mine && (
        <button
          onClick={async () => {
            if (!window.confirm("End this meeting for everyone?")) return;
            try {
              await api.post(`/workspace/${workspaceId}/call/end`);
              toast.success("Meeting ended");
              setActive(null);
            } catch (e) {
              toast.error(e?.response?.data?.detail || "Couldn't end meeting");
            }
          }}
          className="kb-btn-secondary text-sm font-semibold px-3 border-2 border-rose-500 text-rose-600 hover:bg-rose-50 inline-flex items-center gap-1.5"
          data-testid="active-call-quit-btn"
          title="End meeting for all participants"
        >
          <PhoneOff className="w-3.5 h-3.5" /> Quit Meeting
        </button>
      )}
    </div>
  );
}
